import { Link } from "react-router-dom";
import { CheckCircle2, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type { Module } from "@/lib/curriculum";

interface ModuleCardProps {
  module: Module;
  done: number;
  total: number;
}

export function ModuleCard({ module, done, total }: ModuleCardProps) {
  const complete = done >= total && total > 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const moduleXp = module.lessons.reduce((s, l) => s + l.xp, 0);

  return (
    <Link
      to={`/module/${module.id}`}
      className="group block focus-visible:outline-none"
    >
      <Card className="relative h-full overflow-hidden border-2 pt-0 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg group-focus-visible:ring-2 group-focus-visible:ring-ring">
        {/* Banner image */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={module.image}
            alt={`${module.title} illustration`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          <div
            className={cn(
              "absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r",
              module.gradient,
            )}
          />
          {complete && (
            <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-background/90 px-2 py-1 text-xs font-semibold text-primary shadow-sm backdrop-blur">
              <CheckCircle2 className="h-3.5 w-3.5" /> Done
            </span>
          )}
          <div
            className={cn(
              "absolute -bottom-5 left-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br text-2xl shadow-md ring-4 ring-card",
              module.gradient,
            )}
          >
            {module.emoji}
          </div>
        </div>

        <CardContent className="flex h-full flex-col gap-3 px-5 pb-5 pt-7">
          <div className="flex-1">
            <h3 className="text-lg font-bold leading-tight">{module.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {module.tagline}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
              <span>
                {done}/{total} lessons
              </span>
              <span className="flex items-center gap-1">
                <Zap className="h-3 w-3 fill-current text-primary" />
                {moduleXp} XP
              </span>
            </div>
            <Progress value={pct} className="h-2" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
